export const NAME = 'adminV2.childOrganization';

/**
 * Child-org dashboard slice.
 *
 * - `currentOrganization`: `null` until bulk fetch completes; `'all'` when viewing All users;
 *   org object when one child org is selected. Stays `null` when the user lacks admin_user__view.
 * - `usersByOrgId` / `allUsersFlattened`: cache for sidebar + All view; sorted in actions via
 *   `sortRecordsByName` (not here — importing business-logic would cycle through the store).
 * - Sync keyword filters update `filtered*` only and must not flip loading flags (page spinner).
 */
const initialState = {
    currentOrganization: null,
    organizationsForList: [],
    filteredOrganizationsList: [],
    users: [],
    filteredUsers: [],
    usersByOrgId: {},
    allUsersFlattened: [],
    isPageLoading: true,
    isSideBarLoading: false,
    isTableLoading: false,
    ownOrganizationType: '',
};

/**
 * Reduces admin-v2 child-organization dashboard state (org sidebar + users table caches).
 *
 * @param {Object} [state] - Current slice state
 * @param {{ type: string, payload?: Object }} action
 * @returns {Object} Next slice state
 */
export default function reducer(state = initialState, action) {
    // Backfill cache fields when the Redux slice predates them (e.g. HMR without store reset)
    const currentState = {
        ...initialState,
        ...state,
        usersByOrgId: state.usersByOrgId ?? {},
        allUsersFlattened: state.allUsersFlattened ?? [],
    };

    switch (action.type) {
        default:
            return currentState;
        case 'ADMIN_V2__FETCH_CHILD_ORGANIZATION_START': {
            return {
                ...currentState,
                isTableLoading: true,
                users: [],
                filteredUsers: [],
            };
        }
        case 'ADMIN_V2__FETCH_CHILD_ORGANIZATION_END': {
            const organization = action.payload.data.organization;
            // Action builds usersByOrgId + allUsersFlattened with sortRecordsByName
            const usersByOrgId = action.payload.usersByOrgId ?? currentState.usersByOrgId;
            const allUsersFlattened =
                action.payload.allUsersFlattened ?? currentState.allUsersFlattened;
            return {
                ...currentState,
                isTableLoading: false,
                currentOrganization: organization,
                users: organization ? organization.users : [],
                filteredUsers: organization ? organization.users : [],
                usersByOrgId,
                allUsersFlattened,
            };
        }
        case 'ADMIN_V2__FITER_CHILD_USERS_START': {
            // Sync search — do not toggle isTableLoading (avoids page-level spinner flash)
            return currentState;
        }
        case 'ADMIN_V2__FITER_CHILD_USERS_END': {
            return {
                ...currentState,
                filteredUsers: action.payload.data,
            };
        }

        case 'ADMIN_V2__FETCH_CHILD_ORGS_BY_TYPE_START': {
            // Clear both panels so a tab switch does not flash the previous tab's orgs or users
            return {
                ...currentState,
                isSideBarLoading: true,
                isTableLoading: true,
                organizationsForList: [],
                filteredOrganizationsList: [],
                users: [],
                filteredUsers: [],
                allUsersFlattened: [],
                usersByOrgId: {},
            };
        }

        case 'ADMIN_V2__FETCH_CHILD_ORGS_BY_TYPE_END': {
            // Explicit null when users panel unavailable; default 'all' only for older payloads
            const nextCurrentOrganization =
                action.payload.currentOrganization !== undefined
                    ? action.payload.currentOrganization
                    : 'all';

            return {
                ...currentState,
                isSideBarLoading: false,
                isTableLoading: false,
                currentOrganization: nextCurrentOrganization,
                users: action.payload.users,
                filteredUsers: action.payload.users,
                allUsersFlattened: action.payload.allUsersFlattened,
                usersByOrgId: action.payload.usersByOrgId,
                organizationsForList: action.payload.organizations,
                filteredOrganizationsList: action.payload.organizations,
                ownOrganizationType: action.payload.ownOrganizationType,
            };
        }

        case 'ADMIN_V2__SELECT_ALL_ORGANIZATIONS': {
            // Cache-only switch — payload.users is usually allUsersFlattened from actions
            return {
                ...currentState,
                isTableLoading: false,
                currentOrganization: 'all',
                users: action.payload.users,
                filteredUsers: action.payload.users,
            };
        }

        case 'ADMIN_V2__SELECT_ORGANIZATION': {
            return {
                ...currentState,
                isTableLoading: false,
                currentOrganization: action.payload.organization,
                users: action.payload.users,
                filteredUsers: action.payload.users,
            };
        }

        case 'ADMIN_V2__APPLY_SAVED_ORGANIZATION': {
            // Org create/edit without refetch; action may include rebuilt All-users cache
            const { org, users, usersByOrgId, allUsersFlattened } = action.payload;
            return {
                ...currentState,
                isTableLoading: false,
                currentOrganization: { ...org, users },
                users,
                filteredUsers: users,
                usersByOrgId: usersByOrgId ?? currentState.usersByOrgId,
                allUsersFlattened: allUsersFlattened ?? currentState.allUsersFlattened,
            };
        }

        case 'ADMIN_V2__APPLY_SAVED_USER': {
            // Cache-patch after user create/update/delete — no loading flags (no page spinner).
            // Action owns upsert/delete by user.organization + sortRecordsByName flatten.
            const { user, usersByOrgId, allUsersFlattened } = action.payload;
            if (!user || !user.id || !usersByOrgId || !allUsersFlattened) {
                return currentState;
            }

            let nextUsers;
            let nextFilteredUsers;
            if (currentState.currentOrganization === 'all') {
                nextUsers = allUsersFlattened;
                nextFilteredUsers = allUsersFlattened;
            } else if (
                currentState.currentOrganization !== null &&
                currentState.currentOrganization !== 'all'
            ) {
                const selectedOrgId = currentState.currentOrganization.id;
                const orgUsers = usersByOrgId[selectedOrgId] || [];
                nextUsers = orgUsers;
                nextFilteredUsers = orgUsers;
            } else {
                // Users panel never available (no admin_user__view)
                nextUsers = currentState.users;
                nextFilteredUsers = currentState.filteredUsers;
            }

            return {
                ...currentState,
                usersByOrgId,
                allUsersFlattened,
                users: nextUsers,
                filteredUsers: nextFilteredUsers,
            };
        }

        case 'ADMIN_V2__FILTER_CHILD_ORGS_START': {
            // Sync search — do not toggle isSideBarLoading (avoids page spinner flash)
            return currentState;
        }

        case 'ADMIN_V2__FILTER_CHILD_ORGS_END': {
            return {
                ...currentState,
                filteredOrganizationsList: action.payload.data,
            };
        }

        case 'ADMIN_V2__UPSERT_CHILD_ORG': {
            return {
                ...currentState,
                organizationsForList: action.payload.organizations,
                filteredOrganizationsList: action.payload.organizations,
            };
        }

        case 'ADMIN_V2__DEPRECATE_CHILD_ORG': {
            // Local prune after deprecate modal PATCH — no GraphQL refetch
            const { orgId } = action.payload;
            const organizationsForList = currentState.organizationsForList.filter(
                organization => organization.id !== orgId
            );
            const filteredOrganizationsList = currentState.filteredOrganizationsList.filter(
                organization => organization.id !== orgId
            );
            const nextUsersByOrgId = { ...currentState.usersByOrgId };
            delete nextUsersByOrgId[orgId];

            const allUsersFlattened = currentState.allUsersFlattened.filter(
                user => user.organization !== orgId
            );
            const users = currentState.users.filter(user => user.organization !== orgId);
            const filteredUsers = currentState.filteredUsers.filter(
                user => user.organization !== orgId
            );

            // null currentOrganization means the users panel was never available (no admin_user__view)
            const hadUsersPanel = currentState.currentOrganization !== null;
            const deprecatedSelectedOrg =
                currentState.currentOrganization !== null &&
                currentState.currentOrganization !== 'all' &&
                currentState.currentOrganization.id === orgId;

            let nextCurrentOrganization = currentState.currentOrganization;
            let nextUsers = users;
            let nextFilteredUsers = filteredUsers;

            if (deprecatedSelectedOrg) {
                // Fall back to All view when the selected org was removed
                if (hadUsersPanel) {
                    nextCurrentOrganization = 'all';
                    nextUsers = allUsersFlattened;
                    nextFilteredUsers = allUsersFlattened;
                } else {
                    nextCurrentOrganization = null;
                    nextUsers = [];
                    nextFilteredUsers = [];
                }
            }

            return {
                ...currentState,
                organizationsForList,
                filteredOrganizationsList,
                usersByOrgId: nextUsersByOrgId,
                allUsersFlattened,
                currentOrganization: nextCurrentOrganization,
                users: nextUsers,
                filteredUsers: nextFilteredUsers,
            };
        }

        case 'ADMIN_V2__CHANGE_ORG_FILTER_TYPE_START': {
            // Same loading contract as bulk fetch — page-level spinner, lists cleared
            return {
                ...currentState,
                isSideBarLoading: true,
                isTableLoading: true,
                organizationsForList: [],
                filteredOrganizationsList: [],
                users: [],
                filteredUsers: [],
                allUsersFlattened: [],
                usersByOrgId: {},
            };
        }
        case 'ADMIN_V2__CHANGE_ORG_FILTER_TYPE_END': {
            return {
                ...currentState,
                isSideBarLoading: false,
                isTableLoading: false,
            };
        }

        case 'ADMIN_V2__CHILD_ORGANIZATION_DASH_INIT_START': {
            // Page shell renders immediately; page-level BlockLoadGroup covers init + first bulk fetch
            return {
                ...currentState,
                isPageLoading: true,
                isSideBarLoading: true,
                isTableLoading: true,
                organizationsForList: [],
                filteredOrganizationsList: [],
                users: [],
                filteredUsers: [],
                allUsersFlattened: [],
                usersByOrgId: {},
            };
        }

        case 'ADMIN_V2__CHILD_ORGANIZATION_DASH_INIT_END': {
            return {
                ...currentState,
                isPageLoading: false,
            };
        }
    }
}
